import React, { useState, useRef } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { updateUser } from "../../services/firestore";
import { uploadProfilePhoto, uploadTwicCard } from "../../services/storage";
import PlacesAutocomplete from "../../components/PlacesAutocomplete";
import { formatPhone } from "../../utils/phone";
import { enablePushNotifications, disablePushNotifications, getNotificationPermission, sendTestPush } from "../../services/notifications";
import "./GuardLayout.css";

function completionStatus(guard) {
  return {
    photo:   !!guard?.photoUrl,
    phone:   !!guard?.phone,
    twic:    !!(guard?.twicCardUrl && guard?.twicExpiry),
    taxInfo: !!(guard?.taxName && guard?.tin && guard?.address),
  };
}

export default function GuardProfile() {
  const { userProfile, loadUserProfile, logout } = useAuth();
  const [form, setForm] = useState({
    phone:    userProfile?.phone    || "",
    twicExpiry: userProfile?.twicExpiry || "",
    taxName:  userProfile?.taxName  || "",
    tin:      userProfile?.tin      || "",
    address:  userProfile?.address  || "",
  });
  const [saving, setSaving]           = useState(false);
  const [saved, setSaved]             = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingTwic,  setUploadingTwic]  = useState(false);
  const [uploadError, setUploadError] = useState("");
  const photoRef = useRef();
  const twicRef  = useRef();

  const status = completionStatus(userProfile);
  const allComplete = Object.values(status).every(Boolean);

  // Push-notification state. We don't auto-prompt — the guard taps the
  // toggle, which counts as a user gesture and lets the browser show the
  // permission dialog (most browsers block unsolicited prompts).
  const [pushPermission, setPushPermission] = useState("default");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState("");
  const isPushEnabled =
    pushPermission === "granted" &&
    Array.isArray(userProfile?.pushTokens) &&
    userProfile.pushTokens.length > 0;

  React.useEffect(() => {
    getNotificationPermission().then(setPushPermission);
  }, [userProfile?.pushTokens?.length]);

  async function togglePush() {
    setPushBusy(true);
    setPushMsg("");
    if (isPushEnabled) {
      const r = await disablePushNotifications(userProfile.id);
      if (!r.ok) setPushMsg("Couldn't disable: " + r.reason);
      else setPushMsg("Reminders turned off on this device.");
    } else {
      const r = await enablePushNotifications(userProfile.id, (payload) => {
        // Foreground notification — show a quick alert. Could be replaced
        // with a polished toast later.
        const t = payload?.notification?.title || "VeloceFlow";
        const b = payload?.notification?.body || "";
        if (Notification && Notification.permission === "granted") {
          new Notification(t, { body: b, icon: "/logo192.png" });
        }
      });
      if (!r.ok) setPushMsg(r.reason);
      else setPushMsg("Reminders are on for this device.");
    }
    await loadUserProfile(userProfile.id);
    setPushPermission(await getNotificationPermission());
    setPushBusy(false);
  }

  // QA-only "send me a test alert now" — bypasses the day-before scheduler
  // entirely. If this works but the scheduled job doesn't, you know the
  // delivery pipeline is fine and the bug is in the cron/filter logic.
  async function handleTestPush() {
    setPushBusy(true);
    setPushMsg("");
    try {
      const r = await sendTestPush();
      if (r.failure > 0) {
        setPushMsg(`Sent — but ${r.failure} token(s) failed (${(r.errors || []).join(", ")}). Pruned ${r.pruned}.`);
      } else if (r.success > 0) {
        setPushMsg(`Test sent to ${r.success} device(s). Watch for the banner.`);
      } else {
        setPushMsg("No devices got the push. Re-enable reminders to mint a fresh token.");
      }
    } catch (err) {
      setPushMsg("Test failed: " + (err?.message || err));
    }
    setPushBusy(false);
  }

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave() {
    setSaving(true);
    await updateUser(userProfile.id, form);
    await loadUserProfile(userProfile.id);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  }

  async function handlePhotoUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    setUploadError("");
    try {
      const url = await uploadProfilePhoto(file, userProfile.id);
      await updateUser(userProfile.id, { photoUrl: url });
      await loadUserProfile(userProfile.id);
    } catch (err) {
      console.error("Photo upload failed", err);
      setUploadError(`Photo upload failed: ${err?.message || err?.code || "unknown error"}`);
    }
    setUploadingPhoto(false);
  }

  async function handleTwicUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingTwic(true);
    setUploadError("");
    try {
      const url = await uploadTwicCard(file, userProfile.id);
      await updateUser(userProfile.id, { twicCardUrl: url });
      await loadUserProfile(userProfile.id);
    } catch (err) {
      console.error("TWIC upload failed", err);
      setUploadError(`TWIC upload failed: ${err?.message || err?.code || "unknown error"}`);
    }
    setUploadingTwic(false);
  }

  return (
    <div style={{ paddingBottom: 24 }}>
      {/* Profile photo — hard gate */}
      <div style={{
        background: "#0B1C2E", padding: "20px 16px",
        display: "flex", flexDirection: "column", alignItems: "center", gap: 12,
      }}>
        {/* No `capture` → guard can take a new selfie OR pick an existing photo */}
        <input ref={photoRef} type="file" accept="image/*"
          style={{ display: "none" }} onChange={handlePhotoUpload} />

        <div
          onClick={() => photoRef.current?.click()}
          style={{
            width: 80, height: 80, borderRadius: "50%",
            background: userProfile?.photoUrl ? "transparent" : "#1a3050",
            border: status.photo ? "3px solid #4CAF96" : "3px dashed #EF9F27",
            overflow: "hidden", cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center", flexShrink: 0,
          }}
        >
          {userProfile?.photoUrl ? (
            <img src={userProfile.photoUrl} alt="profile" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          ) : (
            <span style={{ fontSize: 28 }}>📷</span>
          )}
        </div>

        <div style={{ textAlign: "center" }}>
          <div style={{ color: "#fff", fontWeight: 700, fontSize: 16 }}>{userProfile?.name || "Guard"}</div>
          <div style={{ color: "#8ab4d4", fontSize: 12 }}>{userProfile?.email}</div>
        </div>

        {!status.photo && (
          <div style={{
            background: "#EF9F27", borderRadius: 8, padding: "8px 14px",
            fontSize: 12, color: "#fff", fontWeight: 600, textAlign: "center",
          }}>
            ⚠️ Profile photo required before grabbing jobs
          </div>
        )}

        <button
          className="g-btn g-btn-ghost g-btn-sm"
          onClick={() => photoRef.current?.click()}
          disabled={uploadingPhoto}
          style={{ background: "rgba(255,255,255,0.1)", color: "#fff", border: "none" }}
        >
          {uploadingPhoto ? "Uploading..." : status.photo ? "Change photo" : "Upload photo"}
        </button>
      </div>

      {/* Upload error */}
      {uploadError && (
        <div style={{ margin: "8px 12px 0", background: "#fff0f0", border: "1px solid #f5a5a5", borderRadius: 8, padding: "10px 13px", fontSize: 12, color: "#a32d2d" }}>
          {uploadError}
        </div>
      )}

      {/* Completion status */}
      <div style={{ margin: "12px 12px 0", background: "#fff", borderRadius: 12, padding: "12px 14px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#888", marginBottom: 10 }}>PROFILE COMPLETION</div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {[
            { key: "photo",   label: "Photo" },
            { key: "phone",   label: "Phone" },
            { key: "twic",    label: "TWIC" },
            { key: "taxInfo", label: "Tax info" },
          ].map(({ key, label }) => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
              <div style={{ width: 10, height: 10, borderRadius: "50%", background: status[key] ? "#2d7a2d" : "#e8e8e8", flexShrink: 0 }} />
              <span style={{ color: status[key] ? "#2d7a2d" : "#aaa" }}>{label}</span>
            </div>
          ))}
          {allComplete && <span style={{ fontSize: 11, color: "#2d7a2d", fontWeight: 600 }}>✓ All complete</span>}
        </div>
      </div>

      {/* Contact */}
      <div className="g-section-label">Contact</div>
      <div className="g-profile-section">
        <div className="g-profile-row">
          <div className="g-profile-label">Phone</div>
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={(e) => set("phone", formatPhone(e.target.value))}
            onBlur={(e) => set("phone", formatPhone(e.target.value))}
            placeholder="(555) 555-5555"
            style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 13, fontWeight: 500 }}
          />
          {form.phone && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#2d7a2d" }} />}
        </div>
      </div>

      {/* Push notifications */}
      <div className="g-section-label">Reminders</div>
      <div className="g-profile-section">
        <div className="g-profile-row" style={{ alignItems: "flex-start" }}>
          <div style={{ flex: 1 }}>
            <div className="g-profile-label">Push reminders on this device</div>
            <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
              {pushPermission === "unsupported"
                ? "This browser doesn't support push — try Chrome/Edge on Android, or install as an app on iOS."
                : pushPermission === "denied"
                  ? "Notifications are blocked. Open site settings in your browser to allow them."
                  : isPushEnabled
                    ? "You'll get a phone alert the day before each detail."
                    : "Tap to allow phone alerts the day before each detail (instead of only seeing them when you open the app)."}
            </div>
            {pushMsg && <div style={{ fontSize: 11, color: "#185FA5", marginTop: 4 }}>{pushMsg}</div>}
          </div>
          <button
            onClick={togglePush}
            disabled={pushBusy || pushPermission === "unsupported" || pushPermission === "denied"}
            className={`g-btn ${isPushEnabled ? "g-btn-ghost" : "g-btn-primary"}`}
            style={{ minWidth: 110 }}
          >
            {pushBusy ? "..." : isPushEnabled ? "Turn off" : "Turn on"}
          </button>
        </div>
        {/* Test button — only visible once reminders are on. Sends a push
            to this device's tokens immediately so you can confirm the
            delivery pipeline works without waiting on the day-before
            scheduler. Hidden when push isn't on (nothing to test). */}
        {isPushEnabled && (
          <div style={{ borderTop: "1px solid #f0f0f0", marginTop: 10, paddingTop: 10, display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={handleTestPush}
              disabled={pushBusy}
              className="g-btn g-btn-ghost g-btn-sm"
              style={{ fontSize: 11 }}
            >
              {pushBusy ? "..." : "Send test alert"}
            </button>
          </div>
        )}
      </div>

      {/* TWIC */}
      <div className="g-section-label">TWIC Card</div>
      <div className="g-profile-section">
        <div className="g-profile-row">
          <div className="g-profile-label">Card upload</div>
          <input ref={twicRef} type="file" accept="image/*,application/pdf"
            style={{ display: "none" }} onChange={handleTwicUpload} />
          <div style={{ flex: 1 }}>
            {userProfile?.twicCardUrl ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 13, color: "#2d7a2d", fontWeight: 500 }}>✓ On file</span>
                <button
                  className="g-btn g-btn-ghost g-btn-sm"
                  style={{ minHeight: 30, padding: "4px 10px", fontSize: 11 }}
                  onClick={() => twicRef.current?.click()}
                  disabled={uploadingTwic}
                >
                  {uploadingTwic ? "Uploading..." : "Replace"}
                </button>
              </div>
            ) : (
              <button
                className="g-btn g-btn-ghost g-btn-sm"
                style={{ minHeight: 36 }}
                onClick={() => twicRef.current?.click()}
                disabled={uploadingTwic}
              >
                {uploadingTwic ? "Uploading..." : "📎 Upload TWIC card"}
              </button>
            )}
          </div>
        </div>
        <div className="g-profile-row">
          <div className="g-profile-label">Expiry date</div>
          <input
            type="date"
            value={form.twicExpiry}
            onChange={(e) => set("twicExpiry", e.target.value)}
            style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 13 }}
          />
          {form.twicExpiry && new Date(form.twicExpiry) < new Date() && (
            <span style={{ fontSize: 11, color: "#a32d2d", fontWeight: 600 }}>Expired</span>
          )}
        </div>
      </div>

      {/* Tax info */}
      <div className="g-section-label">Tax information — 1099</div>
      <div style={{ fontSize: 11, color: "#888", padding: "0 16px 8px", lineHeight: 1.5 }}>
        Required for year-end 1099. Your owner can also edit this section.
      </div>
      <div className="g-profile-section">
        {[
          { label: "Legal name",     key: "taxName",  placeholder: "As shown on tax forms" },
          { label: "TIN / SSN",      key: "tin",      placeholder: "Tax ID (last 4 OK for now)" },
          { label: "Mailing address", key: "address", placeholder: "Street, City, State, ZIP" },
        ].map(({ label, key, placeholder }) => (
          <div key={key} className="g-profile-row">
            <div className="g-profile-label">{label}</div>
            {key === "address" ? (
              <PlacesAutocomplete
                value={form[key]}
                onChange={(addr) => set(key, addr)}
                placeholder={placeholder}
                style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 13 }}
              />
            ) : (
              <input
                value={form[key]}
                onChange={(e) => set(key, e.target.value)}
                placeholder={placeholder}
                style={{ flex: 1, background: "none", border: "none", outline: "none", fontSize: 13 }}
              />
            )}
            {form[key] && <div style={{ width: 8, height: 8, borderRadius: "50%", background: "#2d7a2d" }} />}
          </div>
        ))}
      </div>

      {/* Save */}
      <div style={{ padding: "16px 12px 0" }}>
        <button className="g-btn g-btn-primary" onClick={handleSave} disabled={saving} style={{ fontSize: 15 }}>
          {saved ? "✓ Saved" : saving ? "Saving..." : "Save profile"}
        </button>
      </div>

      {/* Sign out — last item on the screen so a misclick on a primary
          action doesn't accidentally log them out mid-job. Confirm
          dialog as a final guard against fat-finger taps. */}
      <div style={{ padding: "32px 12px 16px", borderTop: "1px solid #f0f0f0", marginTop: 24 }}>
        <button
          onClick={async () => {
            if (!window.confirm("Sign out of VeloceFlow? You'll need to log back in to grab or run jobs.")) return;
            await logout();
          }}
          className="g-btn g-btn-ghost"
          style={{ fontSize: 13, color: "#a32d2d", borderColor: "#e5b8b8" }}
        >
          Sign out
        </button>
        {userProfile?.email && (
          <div style={{ fontSize: 10, color: "#aaa", textAlign: "center", marginTop: 8 }}>
            Signed in as {userProfile.email}
          </div>
        )}
      </div>
    </div>
  );
}
